import { baseQueryWithReauth } from './baseQuery';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Rol } from '../utils/types';

export const adminApi = createApi({
    reducerPath: 'admin',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['roles', 'roleById', 'users', 'userById', 'permissionByRoleId'],
    endpoints: (builder) => ({
        getRoles: builder.query({
            query: () => ({
                url: 'admin/roles'
            }),
            providesTags: ['roles'],
            transformResponse: (response: { data: { roles: any } }) => response?.data?.roles
        }),
        getRoleById: builder.query<any, any>({
            query: (rolId: number) => ({
                url: `admin/roles/${rolId}`
            }),
            providesTags: ['roleById'],
            transformResponse: (response: { data: { rol: any } }) => response?.data
        }),
        addRol: builder.mutation({
            query: (body: Rol) => ({
                url: 'admin/roles',
                method: 'POST',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['roles'])
        }),
        updateRol: builder.mutation({
            query: ({ body, rolId }: any) => ({
                url: `admin/roles/${rolId}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['roles', 'roleById'])
        }),
        getPermissionsByRoleId: builder.query({
            query: (rolId: number) => ({
                url: `admin/roles/${rolId}/permisos`
            }),
            providesTags: ['permissionByRoleId'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        addRolePermissions: builder.mutation({
            query: ({ body, itemId }: any) => ({
                url: `admin/roles/${itemId}/permisos`,
                method: 'POST',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['roles'])
        }),
        updateRolePermissions: builder.mutation({
            query: ({ body, itemId, docTypeId }: any) => ({
                url: `admin/roles/${itemId}/permisos/${docTypeId}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['roles'])
        }),
        deleteRolePermission: builder.mutation({
            query: ({ itemId, docTypeId }: any) => ({
                url: `admin/roles/${itemId}/permisos/${docTypeId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['roles'])
        }),
        deleteRol: builder.mutation({
            query: (rolId: number) => ({
                url: `admin/roles/${rolId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['roles'])
        }),
        getUsers: builder.query({
            query: () => ({
                url: 'admin/usuarios'
            }),
            providesTags: ['users'],
            transformResponse: (response: { data: { usuarios: any } }) => response?.data?.usuarios
        }),
        userCuilValidation: builder.query({
            query: (cuil: string) => ({
                url: `admin/usuarios/validar/${cuil}`
            }),
            transformResponse: (response: { data: any }) => response?.data
        }),
        addUser: builder.mutation({
            query: (body: any) => ({
                url: 'admin/usuarios',
                method: 'POST',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['users'])
        }),
        updateUser: builder.mutation({
            query: ({ body, userId }: any) => ({
                url: `admin/usuarios/${userId}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['users', 'userById'])
        }),
        deleteUser: builder.mutation({
            query: (userId: number) => ({
                url: `admin/usuarios/${userId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['users'])
        }),
        getUserById: builder.query({
            query: (userId: number) => ({
                url: `admin/usuarios/${userId}`
            }),
            providesTags: ['userById'],
            transformResponse: (response: { data: any }) => response?.data
        }),
        addUserPermissions: builder.mutation({
            query: ({ body, itemId }: any) => ({
                url: `admin/usuarios/${itemId}/permisos`,
                method: 'POST',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['users'])
        }),
        updateUserPermissions: builder.mutation({
            query: ({ body, itemId, docTypeId }: any) => ({
                url: `admin/usuarios/${itemId}/permisos/${docTypeId}`,
                method: 'PUT',
                body
            }),
            invalidatesTags: (_, error) => (error ? [] : ['users'])
        }),
        deleteUserPermission: builder.mutation({
            query: ({ itemId, docTypeId }: any) => ({
                url: `admin/usuarios/${itemId}/permisos/${docTypeId}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_, error) => (error ? [] : ['users'])
        })
    })
});

export const {
    useAddUserPermissionsMutation,
    useUpdateUserPermissionsMutation,
    useDeleteUserPermissionMutation,
    useLazyGetPermissionsByRoleIdQuery,
    useGetPermissionsByRoleIdQuery,
    useGetUserByIdQuery,
    useLazyGetUserByIdQuery,
    useAddUserMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useUserCuilValidationQuery,
    useLazyUserCuilValidationQuery,
    useGetUsersQuery,
    useLazyGetRoleByIdQuery,
    useAddRolePermissionsMutation,
    useUpdateRolePermissionsMutation,
    useGetRoleByIdQuery,
    useGetRolesQuery,
    useAddRolMutation,
    useUpdateRolMutation,
    useDeleteRolMutation,
    useDeleteRolePermissionMutation
} = adminApi;
